import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { Popper as MuiPopper, Paper, List, ListItem, ListItemButton, ListItemIcon, ListItemText, CircularProgress, Checkbox, ClickAwayListener } from '@mui/material';
import { Button, Input, Text } from '@fragments';
import './styles.scss';
import { SearchRounded } from '@mui/icons-material';



// Normalizes a string for comparison: lowercase, no accents, no special chars
function normalizeStr(str) {
    return String(str ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s]/g, '')
        .trim();
}

export default function Popper({ controlRef, children, config = {} }) {
    const { multiple = false, searchKeyExtractor = null } = config;

    const hasFooter = multiple || !!searchKeyExtractor;

    const [open, setOpen] = useState(false);
    const [loadingItemId, setLoadingItemId] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [options, setOptions] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Internal pending selection state (only used when multiple=true)
    const [pendingChecked, setPendingChecked] = useState(new Set());
    const initialCheckedRef = useRef(new Set());

    const sideRef = useRef('bottom');

    const handleClose = useCallback(() => {
        setOpen(false);
        setLoadingItemId(null);
        setAnchorEl(null);
        setSearchQuery('');
    }, []);

    // Fires pending onChange callbacks for every option whose state changed, then closes
    const handleConfirm = useCallback((currentOptions, currentPending) => {
        if (multiple) {
            const initial = initialCheckedRef.current;
            currentOptions.forEach(opt => {
                const wasChecked = initial.has(opt.id);
                const isNowChecked = currentPending.has(opt.id);
                if (wasChecked !== isNowChecked && opt.onClick) {
                    opt.onClick();
                }
            });
        }
        handleClose();
    }, [multiple, handleClose]);

    // ClickAway: confirm if multiple, otherwise just close
    const handleClickAway = useCallback(() => {
        if (multiple) {
            handleConfirm(options, pendingChecked);
        } else {
            handleClose();
        }
    }, [multiple, options, pendingChecked, handleConfirm, handleClose]);

    // Expose controlRef API
    useEffect(() => {
        if (!controlRef) return;

        controlRef.current = {
            /**
             * Opens the popper with the given options.
             * @param {Array} opts - [{id, label, icon, variant, disabled, onClick, checked?}]
             * @param {string} side - placement (default: 'bottom')
             * @param {Element} anchorElement - anchor element for positioning
             */
            open(opts, side = 'bottom', anchorElement) {
                sideRef.current = side;
                const safeOpts = opts || [];

                setOptions(safeOpts);
                setAnchorEl(anchorElement);
                setSearchQuery('');

                if (multiple) {
                    const initialSet = new Set(
                        safeOpts.filter(o => o.checked).map(o => o.id)
                    );
                    initialCheckedRef.current = initialSet;
                    setPendingChecked(new Set(initialSet));
                }

                setOpen(true);
            },

            close() {
                handleClose();
            },
        };
    }, [controlRef, multiple, handleClose]);

    // Filter options when searchKeyExtractor is set
    const filteredOptions = useMemo(() => {
        if (!searchKeyExtractor || !searchQuery.trim()) return options;

        const query = normalizeStr(searchQuery);
        const validate = (value) => normalizeStr(value).includes(query);

        return options.filter(opt => searchKeyExtractor(opt, validate));
    }, [options, searchQuery, searchKeyExtractor]);

    // Handle option click
    const handleOptionClick = async (option) => {
        if (option.disabled) return;

        if (multiple) {
            // Only update internal visual state — don't call onClick yet
            setPendingChecked(prev => {
                const next = new Set(prev);
                if (next.has(option.id)) next.delete(option.id);
                else next.add(option.id);
                return next;
            });
            return;
        }

        if (loadingItemId !== null || !option.onClick) return;

        // Default single-select: async call + close handled by option's own onClick
        try {
            setLoadingItemId(option.id);
            await option.onClick();
        } catch (error) {
            console.error('Erro ao executar onClick:', error);
        } finally {
            setLoadingItemId(null);
        }
    };

    const renderIcon = (option) => {
        if (multiple) {
            const isChecked = pendingChecked.has(option.id);
            return (
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                        checked={isChecked}
                        size="small"
                        disableRipple
                        sx={{
                            p: 0,
                            color: 'rgba(255,255,255,0.3)',
                            '&.Mui-checked': { color: 'var(--primary-6)' },
                        }}
                    />
                </ListItemIcon>
            );
        }

        if (option.icon) {
            return (
                <ListItemIcon sx={{ minWidth: 36, color: 'inherit', marginRight: '10px' }}>
                    {option.icon}
                </ListItemIcon>
            );
        }

        return null;
    };

    const renderLoader = (option) => {
        if (!multiple && loadingItemId === option.id) {
            return (
                <CircularProgress size={16} sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.7)' }} />
            );
        }
        return null;
    };

    return (
        <>
            {children}
            <MuiPopper
                open={open && !!anchorEl}
                anchorEl={anchorEl}
                placement={sideRef.current}
                className="custom-popper"
                modifiers={[
                    { name: 'offset', options: { offset: [0, 8] } },
                    { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
                    { name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
                    { name: 'shift', options: { padding: 8 } },
                ]}
            >
                <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                        elevation={8}
                        sx={{
                            backgroundColor: 'var(--surface-9)',
                            borderRadius: '18px',
                            border: '1px solid rgba(255, 255, 255, 0.07)',
                            overflow: 'hidden',
                            minWidth: 200,
                            maxWidth: 'min(300px, calc(100vw - 32px))',
                            width: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Search field */}
                        {searchKeyExtractor && (
                            <div className="popper-search-bar">
                                <Input.Text
                                    end={<SearchRounded />}
                                    value={searchQuery}
                                    onInput={setSearchQuery}
                                    placeholder="Buscar..."
                                    autoFocus
                                />
                            </div>
                        )}

                        {/* Options list */}
                        <List
                            sx={{
                                p: 0,
                                maxHeight: hasFooter ? '232px' : '320px',
                                overflowY: 'auto',
                                flexShrink: 1,
                                '&::-webkit-scrollbar': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                        >
                            {filteredOptions.length === 0 && searchQuery.trim() ? (
                                <ListItem sx={{ py: 2, justifyContent: 'center' }}>
                                    <Text.span
                                        children="Nenhum resultado"
                                        style={{
                                            textAlign: 'center',
                                        }}
                                    />
                                </ListItem>
                            ) : (
                                filteredOptions.map((option, index) => {
                                    const isChecked = multiple && pendingChecked.has(option.id);

                                    return (
                                        <ListItem
                                            key={option.id ?? index}
                                            disablePadding
                                            sx={{
                                                borderBottom: index < filteredOptions.length - 1
                                                    ? '1px solid rgba(255, 255, 255, 0.06)'
                                                    : 'none',
                                            }}
                                        >
                                            <ListItemButton
                                                onClick={() => handleOptionClick(option)}
                                                disabled={option.disabled || (!multiple && loadingItemId !== null)}
                                                className={`popper-option-${option.variant || 'default'}`}
                                                sx={{
                                                    py: 1.1,
                                                    px: 2,
                                                    color: option.disabled
                                                        ? 'rgba(255,255,255,0.4)'
                                                        : 'rgba(255,255,255,0.9)',
                                                    backgroundColor: isChecked
                                                        ? 'var(--primary-bg)'
                                                        : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: option.disabled
                                                            ? 'transparent'
                                                            : isChecked
                                                                ? 'var(--primary-bg)'
                                                                : 'rgba(255,255,255,0.06)',
                                                    },
                                                    '&.Mui-disabled': { opacity: 0.5 },
                                                }}
                                            >
                                                {renderIcon(option)}
                                                <ListItemText
                                                    primary={option.label}
                                                    sx={{
                                                        '& .MuiListItemText-primary': {
                                                            fontSize: '14px',
                                                            fontWeight: isChecked ? 500 : 400,
                                                        },
                                                    }}
                                                />
                                                {renderLoader(option)}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })
                            )}
                        </List>

                        {/* Footer: Aplicar button */}
                        {hasFooter && (
                            <div className="popper-footer">
                                <Button
                                    text="Aplicar"
                                    size="small"
                                    color="inverse"
                                    variant="default"
                                    fullWidth
                                    onClick={() => handleConfirm(options, pendingChecked)}
                                />
                            </div>
                        )}
                    </Paper>
                </ClickAwayListener>
            </MuiPopper>
        </>
    );
}
